type Job<T = unknown> = () => Promise<T>;
type Unsubscribe = () => void;

interface JobQueueOptions {
  concurrent: number;
  onError: (error: Error) => void;
}

interface JobQueue {
  add: <T>(job: Job<T>) => Unsubscribe;
  getPendingCount: () => number;
  getActiveCount: () => number;
}

export function createJobQueue({concurrent, onError}: JobQueueOptions): JobQueue {
  const pendingJobs: Job<unknown>[] = [];
  let activeJobsCount = 0;

  const processQueue = (): void => {
    if (activeJobsCount >= concurrent || pendingJobs.length === 0) return;

    const nextJob = pendingJobs.shift();
    if (!nextJob) return;

    activeJobsCount++;

    nextJob()
      .catch((error) => {
        onError(error);
      })
      .finally(() => {
        activeJobsCount--;
        processQueue(); // Process next job
      });
  };

  const add = <T>(job: Job<T>): Unsubscribe => {
    pendingJobs.push(job);

    Promise.resolve().then(processQueue);

    return () => {
      const jobIndex = pendingJobs.indexOf(job);
      if (jobIndex !== -1) pendingJobs.splice(jobIndex, 1);
    };
  };

  return {
    add,
    getPendingCount: () => pendingJobs.length,
    getActiveCount: () => activeJobsCount,
  };
}
